/* eslint-disable @typescript-eslint/ban-ts-comment */
import { buildCamelCaseMethod, setAtPath } from '@sitecore-search/common';
import RequestData from '../requestData.js';
import { createClass, createSuper, defineProperty, inherits } from './utils.js';
const getSingularString = (str) => str.replace(/s$/, '').replace(/ie$/, 'y');
const getKey = (prop, propConfig, parent) => {
    const { name = prop } = propConfig;
    return parent ? `${parent.key}.${name}` : name;
};
const getMethodKey = (key, prop, parent, propConfig) => {
    if (prop === '__constructor__') {
        // omits parent key
        key = getKey(prop, propConfig, parent?.parent || null);
    }
    return key.replace(/\./g, '_');
};
const getPropInfo = (prop, parent, propConfig) => {
    const { exportKey = [prop], getter, setter } = propConfig;
    const isParentMap = parent?.type === 'map';
    const key = getKey(prop, propConfig, parent);
    const accessProp = parent ? `${parent.accessProp}.${prop}` : prop;
    const exportKeyPath = [...(isParentMap ? ['#KEY#'] : []), ...(prop === '__constructor__' ? [] : exportKey)];
    const propExportKey = parent?.exportKey?.concat(...exportKeyPath) || exportKeyPath;
    const methodKey = getMethodKey(key, prop, parent, propConfig);
    const getterName = getter || buildCamelCaseMethod('get', methodKey);
    const setterName = setter || buildCamelCaseMethod('set', methodKey);
    const config = {
        ...propConfig,
        key,
        accessProp,
        exportKey: propExportKey,
        keyPath: exportKeyPath,
        getter: getterName,
        setter: setterName,
        parent,
    };
    return { config, key, methodKey };
};
const getArgs = (args, { parent }, numberOfArgs, numberOfOptionalArgs = 0) => {
    if (parent?.type === 'map') {
        if (args.length < numberOfArgs - numberOfOptionalArgs + 1) {
            throw new Error('Invalid number of arguments');
        }
        if (typeof args[0] !== 'string') {
            throw new Error(`Invalid map key argument: ${args[0]}`);
        }
        const list = args.slice(1);
        return [
            ...Array(numberOfArgs)
                .fill(undefined)
                .map((_, index) => list[index]),
            args[0],
        ];
    }
    return args;
};
export const build = (parent, properties, data, methods, options) => {
    const propsList = {};
    Object.keys(properties).forEach((prop) => {
        const { key, methodKey, config, config: { accessProp, exportKey, setter, getter, type, model, initialValue }, } = getPropInfo(prop, parent, properties[prop]);
        const typeOptions = { type, ...options };
        propsList[key] = config;
        const value = initialValue ? initialValue() : undefined;
        // TODO: add support to maps initial value
        if (typeof value !== 'undefined' && !key.includes('#KEY#')) {
            setAtPath(data, exportKey.join('.'), value);
        }
        methods.push({
            typeOptions,
            prop: accessProp,
            key: getter,
            value: function (...args) {
                return this.getValue(key, ...getArgs(args, typeOptions, 1, 1));
            },
        });
        methods.push({
            typeOptions,
            prop: accessProp,
            key: setter,
            value: function (...args) {
                // @ts-ignore
                return this.setValue(key, ...getArgs(args, typeOptions, 1));
            },
        });
        methods.push({
            typeOptions,
            prop: accessProp,
            key: buildCamelCaseMethod('reset', methodKey),
            value: function (...args) {
                return this.resetValue(key, ...getArgs(args, typeOptions, 0));
            },
        });
        if (type === 'array') {
            const singularMethodKey = getSingularString(methodKey);
            methods.push({
                typeOptions,
                prop: accessProp,
                key: buildCamelCaseMethod('add', singularMethodKey),
                value: function (...args) {
                    // @ts-ignore
                    return this.addValue(key, ...getArgs(args, typeOptions, 1));
                },
            });
            methods.push({
                typeOptions,
                prop: accessProp,
                key: buildCamelCaseMethod('remove', singularMethodKey),
                value: function (...args) {
                    // @ts-ignore
                    return this.removeValue(key, ...getArgs(args, typeOptions, 2, 1));
                },
            });
        }
        else if (type === 'model' && model) {
            Object.assign(propsList, build(config, model.properties, data, methods, options));
            methods.push({
                typeOptions,
                prop: accessProp,
                key: buildCamelCaseMethod('update', methodKey),
                value: function (...args) {
                    // @ts-ignore
                    return this.updateValue(key, ...getArgs(args, typeOptions, 1));
                },
            });
        }
        else if (type === 'map' && model) {
            Object.assign(propsList, build(config, model.properties, data, methods, { parent: config, ...options }));
        }
    });
    return propsList;
};
export const createModel = (model) => {
    const { name, properties = {} } = model;
    const data = {};
    const methods = [];
    const propsList = build(null, properties, data, methods);
    inherits(ModelClass, RequestData);
    const superInstance = createSuper(ModelClass);
    function ModelClass(...args) {
        // eslint-disable-next-line prefer-spread
        // @ts-ignore
        // eslint-disable-next-line prefer-spread
        const thisInstance = superInstance.call.apply(superInstance, [this].concat(args));
        defineProperty(thisInstance, 'listeners', []);
        defineProperty(thisInstance, 'props', propsList);
        defineProperty(thisInstance, 'data', JSON.parse(JSON.stringify(data)));
        return thisInstance;
    }
    createClass(ModelClass, methods);
    Object.defineProperty(ModelClass, 'name', { value: name });
    // @ts-ignore
    return ModelClass;
};
